import { BaseComponent } from './base-component.js'
import { customElement, property } from 'lit/decorators.js'
import { html, css, CSSResultGroup, TemplateResult } from 'lit'
import { classMap } from 'lit/directives/class-map.js'

@customElement('il-date')
export default class DateComponent extends BaseComponent {
  @property({ type: String, reflect: true })
  date: string = ''

  @property({ type: String, attribute: 'secondary-text' })
  secondaryText: string = ''

  @property({ type: Boolean, attribute: 'highlight-secondary-text' })
  highlightSecondaryText: boolean = false

  @property({ type: Boolean, reflect: true, attribute: 'is-past' })
  isPast: boolean = false

  @property({ type: Boolean, reflect: true })
  available: boolean = false

  @property({ type: Boolean, reflect: true })
  selected: boolean = false

  @property({ type: Boolean })
  selectable: boolean = false

  @property({ type: Boolean, reflect: true })
  restricted: boolean = false

  @property({ type: Boolean, reflect: true, attribute: 'sold-out' })
  soldOut: boolean = false

  @property({ type: Boolean, reflect: false })
  loading: boolean = false

  @property({ type: Boolean, reflect: true })
  skeletonLoading: boolean = false

  @property({ type: String, attribute: 'tooltip-text' })
  tooltipText: string = ''

  @property({ type: Object })
  onClick?: () => void

  @property({ type: String, attribute: 'month-label' })
  monthLabel: string = ''

  dateClicked() {
    this.onClick?.()
  }

  get showTooltip(): boolean {
    return !this.available && this.selectable && !!this.tooltipText
  }

  override render(): TemplateResult {
    const classes = {
      'il-date--selectable': this.selectable && this.available,
      'il-date--available': this.available,
      'il-date--unavailable': !this.isPast && !this.available && !this.selectable,
      'il-date--sold-out': this.soldOut,
      'il-date--restricted': this.restricted,
      'il-date--past': this.isPast,
      'il-date--skeleton': this.loading,
      [this.baseCSSClass]: true,
    }

    return html`
      <button
        data-test-iliad-date
        type="button"
        ?disabled=${!this.available || !this.selectable || this.loading}
        class="il-date il-font-component-bold-16 ${classMap(classes)}"
        @click=${this.dateClicked}
      >
        ${this.monthLabel && !this.loading ? html`<span class="il-date__month-label">${this.monthLabel}</span>` : ''}
        ${this.loading
        ? html`<span class="il-date__skeleton"></span>`
        : html`
              <il-tooltip
                data-testid="il-date__tooltip"
                .disableContentHovering=${!this.showTooltip}
                tooltip-text="${this.tooltipText}"
              >
                <div class="il-date__date-text-container">
                  <span class="il-date__date-text"> ${this.date} </span>
                  ${this.secondaryText
            ? html`<span class="il-date__secondary-text ${this.highlightSecondaryText ? 'il-date__secondary-text--highlighted' : ''}">${this.secondaryText}</span>`
            : ''}
                </div>
              </il-tooltip>
            `}
      </button>
    `
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-date {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 48px;
        position: relative;

        padding: 0;
        background-color: transparent;
        border: none;
        border-radius: 6px;

        color: var(--il-color-neutrals-350);
        outline: none;
      }

      .il-date__date-text-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .il-date__date-text {
        color: var(--il-color-neutrals-350);
      }

      .il-date__secondary-text {
        font-size: var(--il-font-size-12);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);
        color: var(--il-color-neutrals-300);
      }
      .il-date__secondary-text--highlighted {
        color: var(--il-color-success-300);
        font-weight: var(--il-font-weight-bold);
      }

      .il-date[disabled] .il-date__date-text {
        color: var(--il-color-neutrals-250);
      }
      .il-date[disabled] .il-date__secondary-text,
      .il-date.il-date--unavailable .il-date__secondary-text,
      .il-date.il-date--sold-out .il-date__secondary-text {
        opacity: 0;
      }

      .il-date.il-date--selectable:not(.il-date--available) {
        background-color: var(--il-color-neutrals-50);
        border: 2px solid transparent;
        font-weight: 400;
        color: var(--il-color-neutrals-250);
      }

      .il-date.il-date--selectable:not(.il-date--available):hover {
        border-color: transparent;
      }

      .il-date.il-date--selectable {
        background-color: var(--il-color-neutrals-0);
        border: 2px solid var(--il-color-neutrals-200);
      }

      .il-date.il-date--unavailable {
        background-color: var(--il-color-neutrals-0);
        border: 2px solid var(--il-color-neutrals-100);
      }

      .il-date.il-date--unavailable .il-date__date-text {
        color: var(--il-color-neutrals-250);
      }

      .il-date.il-date--sold-out {
        border: 2px solid var(--il-color-neutrals-100);
        background-color: var(--il-color-neutrals-50);
      }

      .il-date.il-date--sold-out .il-date__date-text {
        font-weight: 700;
        color: var(--il-color-neutrals-250);
      }

      .il-date.il-date--restricted {
        background-color: var(--il-color-neutrals-100);
        border: 2px solid var(--il-color-neutrals-200);
        color: var(--il-color-neutrals-300);
      }

      .il-date.il-date--past .il-date__date-text {
        color: var(--il-color-neutrals-250);
      }

      .il-date:hover:not(.il-date--skeleton):not(.il-date--unavailable) {
        border-color: var(--il-color-neutrals-250);
      }

      .il-date:focus-visible:not(.il-date--skeleton) {
        border-color: var(--il-color-accent-200);
        border-width: 2px;
      }

      :host([selected]) .il-date:not(.il-date--skeleton) {
        background-color: var(--il-color-accent-300);
      }
      :host([selected]) .il-date:not(:focus-visible):not(.il-date--skeleton) {
        border: none;
      }
      :host([selected]) .il-date:hover:not(.il-date--skeleton) {
        background-color: var(--il-color-accent-400);
      }

      [data-testid='il-date__tooltip'],
      [data-testid='il-date__tooltip']::part(tooltip-trigger),
      [data-testid='il-date__tooltip']::part(tooltip-content) {
        width: 100%;
        height: 100%;
        cursor: default;
      }

      .il-date.il-date--selectable.il-date--available .il-date__date-text {
        cursor: pointer;
      }

      :host([selected]) .il-date__date-text,
      :host([selected]) .il-date__secondary-text {
        color: var(--il-color-neutrals-0);
      }

      .il-date__month-label {
        display: inline;
        width: 24px;
        white-space: nowrap;
        position: absolute;
        top: 0;
        left: 50%;
        right: 0;
        font-size: 9px;
        line-height: 1;
        text-align: center;
        color: var(--il-color-neutrals-300);
        pointer-events: none;
        transform: translateY(-50%) translateX(-50%);
        background-color: var(--il-color-neutrals-0);
        border-radius: 6px;
      }

      /* skeleton loader styles */
      .il-date.il-date--skeleton {
        cursor: default;
        pointer-events: none;
      }

      .il-date__skeleton {
        width: 100%;
        height: 100%;
        border-radius: 4px;
        border-color: transparent;
        animation: skeleton-shimmer 1.5s ease-in-out infinite;
        background: linear-gradient(
          90deg,
          var(--il-color-neutrals-100) 25%,
          var(--il-color-neutrals-50) 50%,
          var(--il-color-neutrals-100) 75%
        );
        background-size: 200% 100%;
      }
    `,
  ]
}
